import * as React from 'react'
import {Box, Typography, IconButton} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import {secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, SUCCESS, ERROR, PRIMARY_BLUE, withOpacity} from '../../../../styles/foundation/colors'

const hintSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * The module's one drop zone. Seven zones use it: the catalogue upload's
 * "choose your own file" surface, and the Evidence Studio's packing-video slot
 * plus its five named image slots.
 *
 * It lived inside DwsUploadDrawer until the catalogue upload needed the same
 * thing. The alternative was `common/hook-form/RHFFileField`'s own zone, which
 * is the treatment this one now copies — but that layer is module-private, has a
 * seventeen-prop surface, and expects the caller to hand it a built
 * `<input type='file'>` through `children` because a react-hook-form Controller
 * owns the value. freeze-v2 holds no react-hook-form at all, so consuming it
 * would have meant a wrapper re-implementing everything around the Controller.
 * What travelled instead is the LOOK — the tinted icon tile, the two-line
 * prompt, the browse control — on top of the mechanics this module already had.
 *
 * THE INPUT IS THE CLICK TARGET, not a `hidden` input behind a Button. `hidden`
 * resolves to `display: none`, which takes the control out of the tab order and
 * out of the accessibility tree; the visible `Button component='label'` that
 * used to front it is focusable but a label has no keyboard activation
 * behaviour, and MUI's ButtonBase turns Enter and Space into an `onClick` call
 * that nothing was listening for. So these zones were mouse-only. A real input
 * stretched transparently across the zone fixes that at the root: one tab stop,
 * Space and Enter open the picker natively, the whole zone is clickable rather
 * than just the button inside it, and the focus ring below has something real to
 * follow. The remove control is lifted above it with a z-index so a held file
 * can still be cleared.
 *
 * `dragActive` is CONTROLLED by the caller rather than held here, because the
 * Evidence Studio's six zones share one `dragKey` — a keyed compare is what
 * stops the pointer crossing from one slot into its neighbour leaving both lit.
 */
export default function DropZone({
  icon: Icon, label, hint, chooseLabel = 'Choose file', file, error, big, dragActive, accept,
  ariaLabel, removeAriaLabel, onPick, onClear, onDragEnter, onDragOver, onDragLeave, onDrop,
}) {
  const errorId = React.useId()

  // Same accent rule as the reference zone: a problem wins over a held file,
  // and an empty zone is the brand blue that says "put something here".
  const accent = error ? ERROR : file ? SUCCESS : PRIMARY_BLUE
  const tile = big ? 48 : 32

  return (
    <Box
      // Both of these have to preventDefault or the browser takes the drop as a
      // navigation and opens the file in the tab, throwing away every unsaved
      // step of the drawer around this zone. `dragenter` matters on its own
      // because the transparent input covers the zone's padding but not its
      // 1.5px border: crossing that band fires `dragleave`, and re-entering has
      // to light the zone again rather than waiting on the next `dragover`.
      onDragEnter={onDragEnter || onDragOver}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        position: 'relative',
        border: `1.5px dashed ${dragActive ? PRIMARY_BLUE : (error ? ERROR : BORDER)}`,
        borderRadius: '10px',
        p: big ? 2 : 1.25,
        textAlign: 'center',
        bgcolor: dragActive ? withOpacity(PRIMARY_BLUE, 0.06) : file ? withOpacity(SUCCESS, 0.05) : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s, box-shadow 0.15s',

        // Guarded rather than unconditional: a bare `&:hover` outranks the
        // background above at every level of specificity, so a merchant
        // dragging a file — pointer necessarily over the zone — would see the
        // hover tint instead of the drag tint.
        ...(!dragActive && !file && !error && {
          '&:hover': {borderColor: PRIMARY_BLUE, bgcolor: withOpacity(PRIMARY_BLUE, 0.04)},
        }),

        // `:focus-within`, not `:focus-visible`: this box is not the focusable
        // element — the input inside it is — so a rule on the box itself would
        // never match. Tabbing to the zone therefore rings the zone, which is
        // the border a keyboard user needs to see before pressing Space.
        '&:focus-within': {
          borderColor: PRIMARY_BLUE,
          boxShadow: `0 0 0 3px ${withOpacity(PRIMARY_BLUE, 0.18)}`,
        },
      }}
    >
      <Box
        component='input'
        type='file'
        accept={accept}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={onPick}
        sx={{
          position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
          width: '100%', opacity: 0, cursor: 'pointer',
        }}
      />

      {file && (
        <IconButton
          size='small'
          onClick={onClear}
          aria-label={removeAriaLabel}
          sx={{position: 'absolute', top: 2, right: 2, zIndex: 1}}
        >
          <CloseIcon sx={{fontSize: 14}} />
        </IconButton>
      )}

      <Box sx={{
        width: tile, height: tile, borderRadius: '10px', mx: 'auto',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: withOpacity(accent, dragActive ? 0.22 : 0.12), color: accent,
        transition: 'background-color 0.15s',
      }}
      >
        <Icon sx={{fontSize: big ? 26 : 18}} />
      </Box>

      <Typography
        title={file ? file.name : undefined}
        sx={{
          fontFamily: FONT, fontWeight: WEIGHT.semibold, color: NAVY, mt: big ? 1 : 0.5,
          fontSize: big ? SIZE.sm : SIZE['2xs'], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        {file ? file.name : label}
      </Typography>

      {!file && hint && <Typography sx={{...hintSx, mt: 0.25, whiteSpace: 'normal'}}>{hint}</Typography>}

      {/* A span, not a Button. The input above already answers every click and
          keypress in the zone, so a second focusable control here would be a
          second tab stop for one job — and interactive content is exactly what
          this affordance must not be. */}
      <Box component='span' sx={{...secondaryBtnSx, display: 'inline-flex', alignItems: 'center', mt: 1}}>
        {file ? 'Replace' : chooseLabel}
      </Box>

      {error && (
        <Typography id={errorId} role='alert' sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: ERROR, mt: 0.5}}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
